import React from 'react'

import Image from 'next/image'
import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { PersonsDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { formatDateWithSuffix } from '@/utils/formatDateWithSuffix'

import { useEntry } from '../Contentful/entries/Article/Article.hooks'

type Props = {
	dataTestId?: string
	children?: React.ReactNode
	type?: string
}

export function AuthorCard({ dataTestId = 'authorCard', children, type }: Props) {
	const entry = useEntry()
	const { locale = 'en-US', isPreview } = useRouter()
	const slug = 'bigcommerce-team'
	const { data } = useSWR(serializeCacheKey('person', { slug, locale }), () =>
		client({ preview: isPreview }).request(PersonsDocument, {
			where: { slug },
			limit: 1,
			locale,
			preview: isPreview
		})
	)
	if ('error' in entry) return <>{children}</>
	let { author, publishedDate } = entry.data
	if (!author) {
		author = data?.personCollection?.items[0] ?? null
	}
	const bio = author?.bio?.json?.content[0].content[0].value
	const bioText: string = bio
	const authorName = author?.fullName
	const authorImg = author?.avatar?.url
	const dateValidation = formatDateWithSuffix(publishedDate, locale)

	return (
		<>
			{type === 'card' ? (
				<div className={clsx('w-full bg-gray-1300 p-8 md:p-12', { 'min-h-[280px]': bioText })}>
					<div className="flex items-center " data-testid={dataTestId}>
						{authorImg ? (
							<div className="flex-shrink-0 relative min-w-16 min-h-16 w-16 h-16 mr-6 rounded-full overflow-hidden bg-white">
								<Image
									src={authorImg}
									alt={author?.slug ?? 'BigCommerce-image'}
									layout="fill"
									objectFit="cover"
								/>
							</div>
						) : null}
						<div className="flex flex-col justify-center items-center">
							<p className="text-base w-full inline text-bc-black font-bold">{authorName}</p>
						</div>
					</div>

					{bioText && <p className="text-bc-black mt-7 leading-relaxed">{bioText}</p>}
				</div>
			) : (
				<div>
					<h6 className="text-base font-bold text-gray-600 m-0">by {authorName}</h6>
					{dateValidation && <p className="text-xs text-gray-600">{dateValidation}</p>}
				</div>
			)}
		</>
	)
}
